import { useState, useMemo } from 'react'
import { useMe } from '../hooks/useMe'
import { useArchived } from '../contexts/ArchiveContext'
import {
  useEventRisks,
  useCreateEventRisk,
  useUpdateEventRisk,
  useDeleteEventRisk,
  useSignOffEventRisk
} from '../hooks/useRisks'
import { RiskEditor, scoreBand } from './RiskEditor'
import { HazardLibraryPickerModal } from './HazardLibraryPickerModal'
import { Icon } from './Icon'
import type { EventRisk, EventRiskBody } from '../types'

// RisksTab - per-event risk register. Two-column layout (table left, 5×5
// matrix right at lg+). Click a matrix cell to filter the table.

interface Props {
  eventId: number
}

export function RisksTab({ eventId }: Props) {
  const archived = useArchived()
  const { data: me } = useMe()
  const { data, isLoading, error } = useEventRisks(eventId)
  const createMut = useCreateEventRisk(eventId)
  const updateMut = useUpdateEventRisk(eventId)
  const deleteMut = useDeleteEventRisk(eventId)
  const signOffMut = useSignOffEventRisk(eventId)

  const [editing, setEditing] = useState<EventRisk | null>(null)
  const [creating, setCreating] = useState(false)
  const [showLibrary, setShowLibrary] = useState(false)
  // Filter by residual L,S cell. null = no cell filter (show all).
  const [cellFilter, setCellFilter] = useState<{ l: number; s: number } | null>(null)

  // Build the 5x5 matrix lookup: (likelihood, severity) -> ids of residuals
  // landing on that cell.
  const matrix = useMemo(() => {
    const m: Record<string, number[]> = {}
    for (const r of data ?? []) {
      const key = `${r.residualLikelihood}|${r.residualSeverity}`
      if (!m[key]) m[key] = []
      m[key].push(r.id)
    }
    return m
  }, [data])

  const filtered = useMemo(() => {
    if (!data) return []
    if (!cellFilter) return data
    return data.filter(
      (r) =>
        r.residualLikelihood === cellFilter.l &&
        r.residualSeverity === cellFilter.s
    )
  }, [data, cellFilter])

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading risks…</div>
  }
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load risks</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  const totalAll = (data ?? []).length
  const totalUnsigned = (data ?? []).filter((r) => r.signedOff == null).length
  const totalHighResidual = (data ?? []).filter((r) => r.residualScore >= 15).length

  function handleSave(body: EventRiskBody) {
    if (editing) {
      updateMut.mutate(
        { riskId: editing.id, body },
        { onSuccess: () => setEditing(null) }
      )
    } else {
      createMut.mutate(body, { onSuccess: () => setCreating(false) })
    }
  }

  function handleDelete() {
    if (!editing) return
    const ok = window.confirm(`Delete "${editing.hazard}"? This can't be undone.`)
    if (!ok) return
    deleteMut.mutate(editing.id, { onSuccess: () => setEditing(null) })
  }

  function handleSignOff(risk: EventRisk) {
    signOffMut.mutate(risk.id)
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl">Risk register</h2>
          <p className="text-sm text-ink-soft mt-0.5">
            Identified hazards with likelihood × severity scores, controls in
            place, and residual scores. Sign-off captures who accepted the
            residual risk for this event.
            {totalAll > 0 && (
              <span className="ml-2 font-mono">
                {totalAll} risk{totalAll === 1 ? '' : 's'}
                {totalUnsigned > 0 && (
                  <span className="text-oxblood"> · {totalUnsigned} awaiting sign-off</span>
                )}
                {totalHighResidual > 0 && (
                  <span className="text-oxblood"> · {totalHighResidual} score &ge; 15</span>
                )}
              </span>
            )}
          </p>
        </div>
        {!archived && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowLibrary(true)}
              className="px-3 py-2 rounded text-sm font-semibold border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
            >
              + From library
            </button>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="px-3 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
            >
              + Add risk
            </button>
          </div>
        )}
      </div>

      {totalAll === 0 && (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#f8dbdd', color: '#7a2329' }}
          >
            <Icon name="alert-triangle" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No risks recorded</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            Pull common hazards from the library or write fresh ones. Every
            event needs a recorded risk register before it can sign off as
            "In Development".
          </p>
          {!archived && (
            <div className="flex gap-2 justify-center">
              <button
                type="button"
                onClick={() => setShowLibrary(true)}
                className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
              >
                Pull from library
              </button>
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="px-4 py-2 rounded text-sm font-semibold border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
              >
                Add fresh
              </button>
            </div>
          )}
        </div>
      )}

      {totalAll > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-5">
          <div className="bg-white border border-line rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-soft border-b border-line">
                  <th className="px-3 py-2 font-semibold">Hazard</th>
                  <th className="px-3 py-2 font-semibold w-24 text-center">Inherent</th>
                  <th className="px-3 py-2 font-semibold w-24 text-center">Residual</th>
                  <th className="px-3 py-2 font-semibold">Owner</th>
                  <th className="px-3 py-2 font-semibold w-32">Sign-off</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <RiskRow
                    key={r.id}
                    risk={r}
                    canSign={
                      !archived && (!!me?.isAdmin || (me?.dbUserId != null && me.dbUserId === r.owner?.id))
                    }
                    onClick={archived ? undefined : () => setEditing(r)}
                    onSignOff={() => handleSignOff(r)}
                    signingOff={signOffMut.isPending && signOffMut.variables === r.id}
                  />
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-xs italic text-ink-soft">
                      No risks match the matrix filter. Click the cell again to clear.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {cellFilter && (
              <div className="px-3 py-2 text-xs text-ink-soft bg-parchment border-t border-line flex items-center justify-between">
                <span>
                  Filtered: residual L={cellFilter.l}, S={cellFilter.s}
                </span>
                <button
                  type="button"
                  onClick={() => setCellFilter(null)}
                  className="text-xs font-semibold text-green hover:underline"
                >
                  Clear filter
                </button>
              </div>
            )}
          </div>

          <Matrix
            matrix={matrix}
            activeCell={cellFilter}
            onCellClick={(l, s) => {
              const key = `${l}|${s}`
              const hits = matrix[key]?.length ?? 0
              if (hits === 0) return
              if (cellFilter?.l === l && cellFilter?.s === s) {
                setCellFilter(null)
              } else {
                setCellFilter({ l, s })
              }
            }}
          />
        </div>
      )}

      {!archived && (creating || editing) && (
        <RiskEditor
          eventId={eventId}
          risk={editing}
          saving={createMut.isPending || updateMut.isPending}
          deleting={deleteMut.isPending}
          serverError={(createMut.error ?? updateMut.error ?? deleteMut.error) as Error | null}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSave={handleSave}
          onDelete={editing ? handleDelete : undefined}
        />
      )}

      {!archived && showLibrary && (
        <HazardLibraryPickerModal eventId={eventId} onClose={() => setShowLibrary(false)} />
      )}
    </div>
  )
}

function RiskRow({
  risk, canSign, onClick, onSignOff, signingOff
}: {
  risk: EventRisk
  canSign: boolean
  onClick: (() => void) | undefined
  onSignOff: () => void
  signingOff: boolean
}) {
  const inh = scoreBand(risk.inherentScore)
  const res = scoreBand(risk.residualScore)
  const interactive = onClick != null
  return (
    <tr className={'border-b border-line last:border-b-0 transition-colors ' + (interactive ? 'hover:bg-parchment cursor-pointer' : '')}>
      <td className="px-3 py-3 align-top" onClick={onClick}>
        <div className="font-semibold text-sm">{risk.hazard}</div>
        <div className="text-[10px] uppercase tracking-wide text-ink-soft mt-0.5">
          {risk.category}
        </div>
        {risk.controls && (
          <p
            className="text-xs text-ink-soft mt-1"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {risk.controls}
          </p>
        )}
      </td>
      <td className="px-3 py-3 text-center align-top" onClick={onClick}>
        <span
          className="inline-block px-2 py-0.5 rounded text-sm font-bold font-mono"
          style={{ background: inh.bg, color: inh.fg }}
          title={`L=${risk.inherentLikelihood} × S=${risk.inherentSeverity}`}
        >
          {risk.inherentScore}
        </span>
      </td>
      <td className="px-3 py-3 text-center align-top" onClick={onClick}>
        <span
          className="inline-block px-2 py-0.5 rounded text-sm font-bold font-mono"
          style={{ background: res.bg, color: res.fg }}
          title={`L=${risk.residualLikelihood} × S=${risk.residualSeverity}`}
        >
          {risk.residualScore}
        </span>
      </td>
      <td className="px-3 py-3 align-top" onClick={onClick}>
        {risk.owner ? (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-green text-parchment text-[10px] font-semibold flex items-center justify-center">
              {risk.owner.avatarInitials ?? '?'}
            </div>
            <span className="text-xs">{risk.owner.name}</span>
          </div>
        ) : (
          <span className="italic text-xs text-ink-soft">—</span>
        )}
      </td>
      <td className="px-3 py-3 align-top" onClick={(e) => e.stopPropagation()}>
        {risk.signedOff ? (
          <span
            className="text-[11px] text-green font-mono"
            title={`${risk.signedOff.by.name ?? 'someone'} at ${risk.signedOff.at ?? ''}`}
          >
            ✓ {risk.signedOff.by.name ?? 'Signed'}
          </span>
        ) : canSign ? (
          <button
            type="button"
            onClick={onSignOff}
            disabled={signingOff}
            className="text-[11px] font-semibold text-green hover:underline disabled:opacity-50"
          >
            {signingOff ? 'Signing…' : 'Sign off'}
          </button>
        ) : (
          <span className="text-[11px] italic text-ink-soft">Awaiting owner</span>
        )}
      </td>
    </tr>
  )
}

// 5x5 matrix - severity along Y (high at top), likelihood along X (high at right).
function Matrix({
  matrix, activeCell, onCellClick
}: {
  matrix: Record<string, number[]>
  activeCell: { l: number; s: number } | null
  onCellClick: (l: number, s: number) => void
}) {
  return (
    <div className="bg-white border border-line rounded p-4 lg:w-72">
      <h3 className="text-sm font-semibold mb-1">Residual risk matrix</h3>
      <p className="text-[11px] text-ink-soft mb-3">Click a cell to filter the table.</p>
      <div className="flex">
        <div className="flex flex-col-reverse items-center justify-around mr-2 text-[10px] text-ink-soft uppercase tracking-wide font-semibold py-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <span key={s}>S{s}</span>
          ))}
        </div>
        <div className="grid grid-rows-5 gap-0.5 flex-1">
          {[5, 4, 3, 2, 1].map((s) => (
            <div key={s} className="grid grid-cols-5 gap-0.5">
              {[1, 2, 3, 4, 5].map((l) => {
                const key = `${l}|${s}`
                const count = matrix[key]?.length ?? 0
                const band = scoreBand(l * s)
                const active = activeCell?.l === l && activeCell?.s === s
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onCellClick(l, s)}
                    disabled={count === 0}
                    className={
                      'aspect-square flex items-center justify-center text-xs font-bold font-mono rounded transition-all ' +
                      (active ? 'ring-2 ring-green ring-offset-1 ' : '') +
                      (count === 0 ? 'opacity-30 cursor-default ' : 'hover:scale-110 cursor-pointer ')
                    }
                    style={{
                      background: band.bg,
                      color: band.fg
                    }}
                    title={`L=${l} × S=${s} = ${l * s} (${count} risk${count === 1 ? '' : 's'})`}
                  >
                    {count > 0 ? count : ''}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-5 gap-0.5 ml-6 mt-1 text-[10px] text-ink-soft uppercase tracking-wide font-semibold text-center">
        {[1, 2, 3, 4, 5].map((l) => (
          <span key={l}>L{l}</span>
        ))}
      </div>
    </div>
  )
}
